import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  UppyAngularDashboardModalModule,
  UppyAngularDashboardModule,
  UppyAngularDragDropModule,
  UppyAngularProgressBarModule,
  UppyAngularStatusBarModule,
} from '@uppy/angular';
import AwsS3 from '@uppy/aws-s3';
import Uppy from '@uppy/core';
import axios from 'axios';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { Channel } from '../../../../models/channel.model';
import { AuthService } from '../../../../services/auth.service';
import { ChannelService } from '../../../../services/channel.service';
import * as channelActions from '../../../../state/actions/channel.actions';
import { AppState } from '../../../../state/appstate';
import { LoadingDotsComponent } from '../../../components/spinners/loading-dots/loading-dots.component';

@Component({
  selector: 'app-edit-channel',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    RouterLink,
    ReactiveFormsModule,
    LoadingDotsComponent,
    UppyAngularDashboardModalModule,
    UppyAngularDashboardModule,
    UppyAngularDragDropModule,
    UppyAngularProgressBarModule,
    UppyAngularStatusBarModule,
  ],
  templateUrl: './edit-channel.component.html',
  styleUrls: ['./edit-channel.component.css'],
})
export class EditChannelComponent implements OnDestroy {
  updateChannelForm: FormGroup;
  description: FormControl = new FormControl('', Validators.required);
  tagField: FormControl = new FormControl('');

  file: File | undefined = undefined;
  tags: Array<string> = [];

  channelSub: Subscription | undefined;
  channel: Channel | null = null;
  imageUrl: string | undefined;

  working: boolean = false;

  progress: number = 0;
  message: string = '';

  uppy = new Uppy({
    restrictions: {
      maxFileSize: 5242880, // 5 MB
      maxNumberOfFiles: 1,
      minNumberOfFiles: 1,
      allowedFileTypes: ['.jpg', '.jpeg', '.png'],
    },
    // onBeforeFileAdded: (currentFile, files) => {
    //   const modifiedFile = {
    //     ...currentFile,
    //     name:  'yourfilename' + Date.now()
    //   }
    //   return modifiedFile
    // }
  });

  dashboardProps = {
    inline: true,
    proudlyDisplayPoweredByUppy: false,
    // height: 128,
    note: 'Images only (jpg, jpeg, png), up to 5 MB. Only one image is allowed.',
  };
  showModal: boolean = false;
  dashboardModalProps = {
    target: document.body,
    onRequestCloseModal: (): void => {
      this.showModal = false;
    },
  };

  ngOnDestroy(): void {
    this.channelSub?.unsubscribe();
  }

  constructor(
    private store: Store<AppState>,
    private channelService: ChannelService,
    private auth: AuthService,
    private fbAuth: Auth,
    private toastr: ToastrService
  ) {
    this.updateChannelForm = new FormGroup({
      description: this.description,
      tags: this.tagField,
    });
    this.channelSub = this.store
      .select((state) => state.channel)
      .subscribe((channelState) => {
        this.channel = channelState.channel;
        this.imageUrl = channelState.channel?.imageUrl;
        this.working = channelState.loading;
        this.updateChannelForm
          .get('description')
          ?.setValue(channelState.channel?.description);
        this.tags = channelState.channel?.tags ?? [];
      });

    this.uppy.use(AwsS3, {
      getUploadParameters: this.getUploadParameters.bind(this),
      shouldUseMultipart: false,
    });

    this.uppy.on('upload-success', (file, response) => {
      this.toastr.success(
        'Channel image updated in server. Kindly refresh to view changes.',
        'Image uploaded successfully'
      );
    });

    this.uppy.on('upload-error', (file, error, response) => {
      this.toastr.error(
        'Channel image could not be updated. Please try again later.',
        'Image upload Failed'
      );
    });
  }

  async getUploadParameters(file: any, options: any): Promise<any> {
    try {
      const fileExtn: string | undefined = file.name?.split('.')?.pop();
      const token: string | undefined =
        await this.fbAuth.currentUser?.getIdToken();

      const response = await axios.get<any>(
        environment['AWS_CHANNEL_IMAGE_SIGNEDURL']!,
        {
          params: {
            uid: this.auth.getUid(),
            name: this.channel?.name,
            fileExtension: fileExtn,
            id: this.channel?.id,
            aspectRatio: '1',
          },
          headers: {
            'x-api-key': environment['AWS_API_KEY']!,
            AuthorizationToken: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!(response.status === 200))
        throw new Error('Unsuccessful request', { cause: response });

      // Parse the JSON response.
      const data = await response.data;

      return {
        method: 'PUT',
        url: response.data.imageUrl,
        headers: {
          'content-Type': `image/${fileExtn}`,
        },
      };
    } catch (error) {
      console.error(`Error getting upload parameters: ${error}`);
      throw error;
    }
  }

  removeFile() {
    this.imageUrl = this.channel?.imageUrl;
    this.file = undefined;
  }

  addTag() {
    this.updateChannelForm.get('tagField')?.setErrors(null);
    if (this.updateChannelForm.get('tagField')?.value === null) return;
    const fieldValue: string = this.updateChannelForm
      .get('tagField')
      ?.value.trim();
    if (fieldValue === '') return;
    if (fieldValue.includes(' '))
      return this.updateChannelForm.controls['tags'].setErrors({
        invalidValue: true,
      });
    if ((this.tags.length ?? 0) >= 10)
      return this.updateChannelForm.controls['tags'].setErrors({
        maxValuesAllowed: true,
      });
    this.tags = [...this.tags, fieldValue];
    this.tagField.setValue('');
    this.tagField.markAsPristine();
  }

  removeTag(tag: number) {
    this.tags.splice(tag, 1);
  }

  refreshChannel() {
    this.store.dispatch(channelActions.loadChannel());
  }

  updateChannel() {
    let tempChannel: Channel = {
      id: this.channel?.id!,
      name: this.channel?.name!,
      description: this.updateChannelForm.get('description')?.value,
      tags: this.tags,
    };
    this.store.dispatch(
      channelActions.updateChannel({
        channel: tempChannel,
      })
    );
  }

  deleteChannel() {
    this.store.dispatch(
      channelActions.deleteChannel({
        id: this.channel?.id!,
      })
    );
  }
}
